import { useEffect, useState } from 'react';
import Modal from '@/components/base/Modal';
import { useAppStore } from '@/store/AppStore';
import { PAYMENT_METHODS } from '@/mocks/constants';
import type { Payment, PaymentMethod } from '@/types';

interface PaymentFormProps {
  open: boolean;
  onClose: () => void;
  prefillCustomer?: string;
  prefillJobId?: string;
}

const inputClass =
  'h-10 w-full rounded-md border border-background-200 bg-white px-3 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100';
const labelClass = 'mb-1.5 block text-xs font-semibold text-foreground-600';

export default function PaymentForm({ open, onClose, prefillCustomer, prefillJobId }: PaymentFormProps) {
  const { customers, jobs, addPayment } = useAppStore();
  const [customerName, setCustomerName] = useState(prefillCustomer ?? '');
  const [jobId, setJobId] = useState(prefillJobId ?? '');
  const [quoteId, setQuoteId] = useState('');
  const [date, setDate] = useState('26 Ağustos 2026');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<PaymentMethod>('Havale / EFT');
  const [description, setDescription] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [error, setError] = useState('');

  function reset() {
    setCustomerName(prefillCustomer ?? '');
    setJobId(prefillJobId ?? '');
    setQuoteId('');
    setAmount('');
    setMethod('Havale / EFT');
    setDescription('');
    setInternalNote('');
    setError('');
  }

  useEffect(() => {
    if (open) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefillCustomer, prefillJobId]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const numericAmount = Number(amount);
    if (!customerName || !numericAmount || numericAmount <= 0) {
      setError('Müşteri ve geçerli bir tutar girilmelidir.');
      return;
    }
    const payment: Payment = {
      id: `P-2026-${String(Date.now()).slice(-4)}`,
      customerName,
      jobId,
      quoteId,
      date,
      amount: numericAmount,
      method,
      description,
      internalNote,
    };
    addPayment(payment);
    reset();
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Ödeme Kaydet"
      maxWidth="max-w-xl"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-background-200 px-4 py-2 text-sm font-semibold text-foreground-700 hover:bg-background-100"
          >
            Vazgeç
          </button>
          <button
            type="submit"
            form="payment-form"
            className="rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-600"
          >
            Kaydet
          </button>
        </>
      }
    >
      <form id="payment-form" onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className={labelClass}>Müşteri</label>
            <select
              className={inputClass}
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
            >
              <option value="">Müşteri seç</option>
              {customers.map((c) => (
                <option key={c.id} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>İlgili İş</label>
            <select className={inputClass} value={jobId} onChange={(e) => setJobId(e.target.value)}>
              <option value="">Seçilmedi</option>
              {jobs.map((j) => (
                <option key={j.id} value={j.id}>
                  {j.id} — {j.customerName}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>İlgili Teklif</label>
            <input
              className={inputClass}
              value={quoteId}
              onChange={(e) => setQuoteId(e.target.value)}
              placeholder="VP-2026-0000"
            />
          </div>
          <div>
            <label className={labelClass}>Ödeme Tarihi</label>
            <input className={inputClass} value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div>
            <label className={labelClass}>Tutar (TL)</label>
            <input
              className={inputClass}
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0"
            />
          </div>
          <div>
            <label className={labelClass}>Ödeme Yöntemi</label>
            <select
              className={inputClass}
              value={method}
              onChange={(e) => setMethod(e.target.value as PaymentMethod)}
            >
              {PAYMENT_METHODS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label className={labelClass}>Açıklama</label>
          <input
            className={inputClass}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Ödeme açıklaması"
          />
        </div>
        <div>
          <label className={labelClass}>Dahili Not</label>
          <textarea
            className="h-20 w-full resize-none rounded-md border border-background-200 bg-white px-3 py-2 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
            value={internalNote}
            onChange={(e) => setInternalNote(e.target.value)}
            placeholder="Dahili not (opsiyonel)"
          />
        </div>
        {error && (
          <p className="rounded-md bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">{error}</p>
        )}
      </form>
    </Modal>
  );
}